import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { DataManagerService } from '../../../../../data/services/DataManager/data-manager.service';
import { AppConstants } from '../../../../../shared/constants/app-constants';
import { TopbarSearchResult } from '../../topbar-search.model';
import { SearchProvider } from '../record-search.model';
import { formatUsd, joinParts } from './list-search';

/** Deposit detail answers from MySQL in about a second; no Redshift path, so the default timeout. */
const DEPOSIT_TIMEOUT_MS = 15000;

/** PayPal deposit reference: YYYYMMDD of the settlement day (e.g. 20261019). */
const DEPOSIT_REFERENCE = /^(20\d{2})(\d{2})(\d{2})$/;
const COUNT = new Intl.NumberFormat('en-US');

/**
 * Deposits — PayPal merchants only.
 *
 * PayPal merchants have no Batch Transactions page, so the Batches group is hidden for them (see
 * batchesProvider). Their batch / deposit reference is the settlement day as YYYYMMDD, and the
 * deposit lives on Deposits (Deposits summary → /balance/deposits-detail). So an 8-digit query that
 * is a real calendar day asks GET /deposit/details?processing_date=<day> — the request the deposit
 * detail page makes — and, when that day has a deposit, offers it, opening the same page as the
 * reference link on Deposits summary. TSYS / Fiserv merchants keep finding batches in Batches.
 *
 * Only the amount, the day and the transaction count are shown; no card data.
 */
export function depositsProvider(dataManager: DataManagerService): SearchProvider {
  return {
    group: 'Deposits',
    parent: 'balance',
    visible: ctx => !!ctx.paypalAccount,
    accepts: query => !!settlementDay(query),
    search: query => depositDetails(dataManager, settlementDay(query)).pipe(
      map(data => toResults(data, query, settlementDay(query)))
    )
  };
}

/** YYYYMMDD → YYYY-MM-DD when it is a real calendar day, otherwise null. */
export function settlementDay(query: string): string | null {
  const m = DEPOSIT_REFERENCE.exec((query || '').trim());
  if (!m) {
    return null;
  }
  const [, y, mo, d] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  const real = date.getFullYear() === Number(y) && date.getMonth() === Number(mo) - 1 && date.getDate() === Number(d);
  return real ? `${y}-${mo}-${d}` : null;
}

function depositDetails(dataManager: DataManagerService, day: string): Observable<any> {
  // the same endpoint constant as DepositsDetailComponent, so the search and the page cannot drift
  const url = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_DEPOSIT_DETAILS + '?processing_date=' + encodeURIComponent(day);
  return dataManager.getRequest(url, 0).pipe(
    timeout(DEPOSIT_TIMEOUT_MS),
    map(res => res?.data ?? null),
    catchError(() => of(null))
  );
}

/** One result when the day has a deposit: transactions, or a non-zero net amount (e.g. fees only). */
function toResults(data: any, reference: string, day: string): TopbarSearchResult[] {
  if (!data || typeof data !== 'object') {
    return [];
  }
  const rows = Array.isArray(data.detailedData) ? data.detailedData : [];
  const totals = Array.isArray(data.totals) ? data.totals[0] : data.totals;
  const net = Number(totals?.netDeposit);
  if (!rows.length && !net) {
    return [];
  }
  const count = rows.length;
  return [{
    group: 'Deposits',
    // Net deposited, in cents, as the deposit detail's headline shows it.
    label: joinParts('Deposit ' + reference, Number.isFinite(net) ? formatUsd(net / 100) : ''),
    sublabel: joinParts(day, COUNT.format(count) + (count === 1 ? ' transaction' : ' transactions')),
    route: ['/balance/deposits-detail'],
    queryParams: { date: day, reference_number: reference }
  }];
}
